import type {
  FlightCardData,
  FlightJourneySegment,
  PriceConditions,
  PriceConditionsRuleDebug,
  PriceJourney,
  PriceLuggage,
  PriceSegment,
  TransformedPriceResponse,
} from '@massilia/queries'

import { format, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'

import { formatTime } from '@/utils/format-time'

// ── Types ──

/**
 * Vue unique dont se sert la sidebar « Détail du tarif ».
 *
 * Deux origines possibles : la réponse price d'Affidium (confirmée) ou l'offre que le
 * client vient de cliquer sur la page résultats (provisoire, le temps que le price
 * réponde — plusieurs secondes). `conditions` n'existe que dans la réponse price :
 * c'est la seule information qu'on ne sait pas anticiper.
 */
export interface BookingPriceView {
  conditions?: PriceConditions
  conditionsRuleDebug?: PriceConditionsRuleDebug
  journeys: PriceJourney[]
  luggage?: PriceLuggage
  passengers: BookingPricePassengerGroup[]
  totalFlightPriceCents: number
}

interface BookingPricePassengerGroup {
  count: number
  /** `Adult | Child | Infant | Senior | Youngster` — la recherche connaît plus de types que le price. */
  type: string
  unitPrice: string
}

// ── Mappers ──

/**
 * Aperçu construit depuis l'offre sélectionnée, strictement décoratif : il n'alimente
 * ni une soumission ni une validation, et le price confirmé le remplace en entier.
 */
export function toPricePreview(
  flight: FlightCardData,
  selectedJourneyKeys: string[]
): BookingPriceView {
  const segments = resolveSegments(flight, selectedJourneyKeys)

  return {
    journeys: segments.map((segment, index) =>
      toPreviewJourney(flight, segment, index, segments.length)
    ),
    luggage: {
      cabinBag: {
        included: flight.minCabinBags > 0,
        quantity: flight.minCabinBags,
        ...(flight.minCabinBagsWeightKg != null && { weightKg: flight.minCabinBagsWeightKg }),
      },
      checkedBags: {
        included: flight.minCheckedBags > 0,
        quantity: flight.minCheckedBags,
        ...(flight.minCheckedBagsWeightKg != null && {
          weightKgPerBag: flight.minCheckedBagsWeightKg,
        }),
      },
    },
    passengers: flight.pricing.perPassenger.map((group) => ({
      count: group.count,
      type: group.type,
      unitPrice: group.pricePerPerson.amount,
    })),
    totalFlightPriceCents: flight.pricing.total.value,
  }
}

/** Vue confirmée, construite depuis la réponse price persistée sur la session. */
export function toPriceView(priceData: TransformedPriceResponse): BookingPriceView {
  return {
    conditions: priceData.conditions,
    conditionsRuleDebug: priceData.conditionsRuleDebug,
    journeys: priceData.journeys,
    luggage: priceData.luggage,
    passengers: priceData.passengers.map((group) => ({
      count: group.count,
      type: group.type,
      unitPrice: group.unitPrice,
    })),
    totalFlightPriceCents: Math.round(parseFloat(priceData.totalPrice.amount) * 100),
  }
}

// ── Helpers ──

/**
 * `d MMMM yyyy` en français, comme le transformer du price côté serveur. `parseISO`
 * plutôt que `new Date` : une date seule (`2026-01-10`) doit être lue en heure locale,
 * sinon elle recule d'un jour dans les fuseaux derrière UTC.
 */
function formatLongDate(date: string): string {
  if (!date) return ''
  try {
    return format(parseISO(date), 'd MMMM yyyy', { locale: fr })
  } catch {
    return date
  }
}

function isoDate(dateTime: string): string {
  try {
    return format(new Date(dateTime), 'yyyy-MM-dd')
  } catch {
    return ''
  }
}

/** `Marseille (MRS)`, format retenu par le price pour les aéroports. */
function locationLabel(city: string, airportCode: string): string {
  return city ? `${city} (${airportCode})` : airportCode
}

/**
 * Trajets réellement choisis par le client. Repli sur les trajets de l'offre si une clé
 * ne retrouve pas son segment : mieux vaut un aperçu approximatif qu'un bloc vide.
 */
function resolveSegments(
  flight: FlightCardData,
  selectedJourneyKeys: string[]
): FlightJourneySegment[] {
  const selected = selectedJourneyKeys
    .map((key) => flight.journeySegments[key])
    .filter((segment): segment is FlightJourneySegment => !!segment)

  if (selected.length > 0) return selected
  if (flight.segments && flight.segments.length > 0) return flight.segments
  return [flight.outbound, flight.return].filter(
    (segment): segment is FlightJourneySegment => !!segment
  )
}

function toPreviewJourney(
  flight: FlightCardData,
  segment: FlightJourneySegment,
  index: number,
  count: number
): PriceJourney {
  return {
    carrier: {
      name: flight.airline,
      code: flight.airlineCode,
      logoUrl: flight.logoUrl ?? null,
    },
    date: formatLongDate(segment.departureDate),
    flightNumber: segment.legs[0]?.flightNumber ?? '',
    legs: segment.legs,
    segments: toPreviewSegments(segment),
    stops: segment.stops,
    totalDuration: segment.totalDuration,
    // Même règle que le transformer serveur, pour que l'aperçu et le confirmé
    // produisent les mêmes libellés (« Aller », « Retour », « Vol 3 »).
    type: count === 1 || index === 0 ? 'outbound' : index === 1 ? 'return' : 'segment',
  }
}

/**
 * Un `PriceSegment` par vol du trajet. Repli sur un segment agrégé (départ du premier
 * vol → arrivée du dernier) quand l'offre ne détaille pas ses legs.
 */
function toPreviewSegments(segment: FlightJourneySegment): PriceSegment[] {
  if (segment.legs.length === 0) {
    return [
      {
        arrival: {
          airport: locationLabel(segment.arrivalCity, segment.arrivalAirport),
          airportCode: segment.arrivalAirport,
          date: formatLongDate(segment.arrivalDate),
          rawDate: segment.arrivalDate,
          time: formatTime(segment.arrivalTime),
        },
        departure: {
          airport: locationLabel(segment.departureCity, segment.departureAirport),
          airportCode: segment.departureAirport,
          date: formatLongDate(segment.departureDate),
          rawDate: segment.departureDate,
          time: formatTime(segment.departureTime),
        },
        duration: segment.totalDuration,
        isNextDay: segment.arrivalDate !== segment.departureDate,
        stops: segment.stops,
      },
    ]
  }

  return segment.legs.map((leg) => {
    const departureDate = isoDate(leg.departureTime)
    const arrivalDate = isoDate(leg.arrivalTime)

    return {
      arrival: {
        airport: locationLabel(leg.arrivalCity, leg.arrivalAirport),
        airportCode: leg.arrivalAirport,
        date: formatLongDate(arrivalDate),
        rawDate: arrivalDate,
        time: formatTime(leg.arrivalTime),
      },
      departure: {
        airport: locationLabel(leg.departureCity, leg.departureAirport),
        airportCode: leg.departureAirport,
        date: formatLongDate(departureDate),
        rawDate: departureDate,
        time: formatTime(leg.departureTime),
      },
      duration: leg.duration,
      isNextDay: arrivalDate !== departureDate,
      stops: 0,
    }
  })
}
